/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { MouseEvent } from 'react';
import * as React from 'react';

import { BasicCard } from '@coursera-web/cds-labs';

import type { TrackingData } from 'js/lib/retracked';

import type { LinkProps } from '@coursera/cds-core';
import { Link, Skeleton, Tag, Typography2, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon, SuccessIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { SKILL_LEVEL_SEGMENTS, getLevelName } from 'bundles/learner-skills/utils';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/page-config/HeaderLevelContext';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import ProgressTracker from 'bundles/skills-common/private/progress-tracker/ProgressTracker';
import { ProgressTrackerMyScorePopoverWithScore } from 'bundles/skills-common/private/progress-tracker/ProgressTrackerMyScorePopover';
import UnscorableProgressTracker from 'bundles/skills-common/private/progress-tracker/UnscorableProgressTracker';
import { generateSkillsPageUrl } from 'bundles/skills-common/private/utils/skillLinkUtils';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

import SkillIcon from './SkillIcon';
import type { SkillProgress } from './withSkillsData';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

const styles = {
  skillCard: css`
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    padding: var(--cds-spacing-300);
    cursor: pointer;
    border-radius: var(--cds-spacing-200);
  `,
  skillName: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  levelsetCompleted: css`
    ${breakpoints.up('sm')} {
      margin-left: auto;
    }
    ${breakpoints.down('xs')} {
      flex-basis: 100%;
      margin-top: var(--cds-spacing-100);
    }

    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
  links: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-300);
    margin-top: var(--cds-spacing-200);
  `,
  link: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
    ${breakpoints.down('sm')} {
      width: 100%;
    }
  `,
};

type Props = {
  className?: string;
  skill: SkillProgress;
  programSlug: string;
  trackingData: TrackingData;
  trackingName: string;
  size: 'small' | 'large';
  source: string;
  currentRefinement?: string | undefined;
  hideLinks?: boolean;
};

const SkillCardPlaceholder = ({ className }: Pick<Props, 'className'>) => {
  return (
    <BasicCard className={className} css={styles.skillCard}>
      <Skeleton height={106} data-testid="placeholder-shimmer" />
    </BasicCard>
  );
};

const SkillCard = (props: Props) => {
  const track = useTracker();
  const { nextLevel } = useHeaderLevel();

  const { currentRefinement } = props;
  const { skillId, skillName, score } = props.skill;
  const { order } = props.trackingData;

  const levelsetCompleted =
    props.skill.levelsetStatus === 'COMPLETED_ALL' || props.skill.levelsetStatus === 'COMPLETED_ONCE';
  const canTakeLevelset =
    props.skill.levelsetStatus !== 'COMPLETED_ALL' && props.skill.levelsetStatus !== 'UNAVAILABLE';

  const skillUrl = generateSkillsPageUrl(props.skill.skillId, props.programSlug);

  const eventingV3SkillData = {
    skillCard: {
      index: order,
      skillScore: score,
    },
    skill: {
      id: skillId,
      name: skillName,
    },
    ...(props.trackingData?.pageSection && { pageSection: props.trackingData.pageSection }),
    ...(currentRefinement && {
      searchedQuery: currentRefinement,
    }),
  };

  const skillCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_skill_card',
    eventingV3SkillData
  );

  const handleSkillCardClick = () => {
    track('click_skill_card', eventingV3SkillData);
    redirectClientOrServer(`${skillUrl}?source=${props.source}`);
  };

  const handleTrackedLinkClick = (e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => {
    e.stopPropagation();
    track('click_skill_card', eventingV3SkillData);
  };

  return (
    <BasicCard
      ref={skillCardRef}
      className={props.className}
      css={styles.skillCard}
      onClick={handleSkillCardClick}
      data-testid={props.trackingName}
    >
      <TrackedDiv trackClicks withVisibilityTracking trackingName={props.trackingName} data={props.trackingData}>
        <Typography2 variant="subtitleMedium" component={nextLevel} css={styles.skillName}>
          <SkillIcon />
          {props.skill.skillName}
        </Typography2>
        {props.skill.isEligibleForScoring ? (
          <ProgressTracker
            forScoring
            hideTarget
            wrapHeader
            score={props.skill.score}
            segments={SKILL_LEVEL_SEGMENTS}
            afterPersonalScore={
              props.skill.score ? <Tag priority="secondary">{getLevelName(props.skill.score)}</Tag> : undefined
            }
            endOfHeader={
              props.size === 'large' && levelsetCompleted ? (
                <div css={styles.levelsetCompleted}>
                  <SuccessIcon color="success" size="small" />
                  <Typography2 color="supportText" variant="bodySecondary" component="span">
                    {_t('LevelSet completed')}
                  </Typography2>
                </div>
              ) : undefined
            }
            scoreTooltip={
              <ProgressTrackerMyScorePopoverWithScore
                score={props.skill.score}
                skillId={props.skill.skillId}
                skillName={props.skill.skillName}
              />
            }
          />
        ) : (
          <UnscorableProgressTracker />
        )}

        {!props.hideLinks && (
          <div css={styles.links}>
            <TrackedLink
              {...Link.defaultProps}
              css={styles.link}
              tabIndex={0}
              trackingName={`${props.trackingName}_link`}
              trackingData={props.trackingData}
              href={`${skillUrl}?source=${props.source}`}
              variant="quiet"
              onClick={(e) => handleTrackedLinkClick(e)}
            >
              <Typography2
                color="inherit"
                variant="subtitleMedium"
                component="span"
                aria-label={_t('View recommendations for #{skillName}', { skillName: props.skill.skillName })}
              >
                {_t('View recommendations')}
              </Typography2>
              <ArrowNextIcon />
            </TrackedLink>
            {props.size === 'large' && canTakeLevelset && (
              <TrackedLink
                {...Link.defaultProps}
                tabIndex={0}
                css={styles.link}
                trackingName={`${props.trackingName}_levelset`}
                variant="quiet"
                trackingData={props.trackingData}
                href={`/levelsets/${props.skill.skillId}?source=${props.source}&programSlug=${props.programSlug}&skillId=${props.skill.skillId}`}
                onClick={(e) => handleTrackedLinkClick(e)}
              >
                <Typography2 color="inherit" variant="subtitleMedium" component="span">
                  {props.skill.levelsetStatus === 'NOT_STARTED' && _t('Check your level')}
                  {props.skill.levelsetStatus === 'COMPLETED_ONCE' && _t('Retake assessment')}
                  {props.skill.levelsetStatus === 'IN_PROGRESS' && _t('Finish assessment')}
                </Typography2>
                <ArrowNextIcon />
              </TrackedLink>
            )}
          </div>
        )}
      </TrackedDiv>
    </BasicCard>
  );
};

export { SkillCardPlaceholder };
export default SkillCard;
